// Static reference data for the Nginx Log Analyzer & Pattern Reference tool.
// All data is curated and verified: every pattern's `regex` matches its
// `example` line, and every Alert24 config block is valid JSON whose embedded
// `pattern` (after JSON un-escaping) equals the documented regex.

export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

export interface LogField {
  /** The literal token as it appears in the example line. */
  token: string;
  /** The nginx variable that produced it. */
  variable: string;
  /** Plain-English explanation. */
  description: string;
}

export interface SeverityLevel {
  level: string;
  meaning: string;
}

export interface NginxPattern {
  id: string;
  name: string;
  /** Which log this pattern is meant to run against. */
  log: 'access' | 'error';
  /** JavaScript regex source (no surrounding slashes). */
  regex: string;
  /** Regex flags to use in the paste-and-test matcher. */
  flags: string;
  /** What the pattern catches. */
  catches: string;
  /** A real log line that the regex matches. */
  example: string;
  severity: Severity;
  /** Alert24 server-agent log_searches entry (file source). */
  agentConfig: string;
  /** Optional matching alert_rules example from agent-log-searches.md. */
  alertRule?: string;
}

// ---------------------------------------------------------------------------
// SECTION 1 — Log format reference
// ---------------------------------------------------------------------------

// nginx default "combined" access log format string:
//   log_format combined '$remote_addr - $remote_user [$time_local] '
//                       '"$request" $status $body_bytes_sent '
//                       '"$http_referer" "$http_user_agent"';
export const COMBINED_EXAMPLE =
  '192.0.2.10 - alice [10/Oct/2024:13:55:36 +0000] "GET /api/orders?id=42 HTTP/1.1" 200 2326 "https://example.com/cart" "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36"';

export const COMBINED_FIELDS: LogField[] = [
  {
    token: '192.0.2.10',
    variable: '$remote_addr',
    description:
      'Client IP address. Behind a proxy/load balancer this is the proxy IP unless you log $http_x_forwarded_for or use the real_ip module.',
  },
  {
    token: '-',
    variable: '(identd)',
    description:
      'RFC 1413 identity of the client. Almost always "-" — nginx does not perform ident lookups; the field exists only for historical Common Log Format compatibility.',
  },
  {
    token: 'alice',
    variable: '$remote_user',
    description:
      'Username supplied via HTTP Basic authentication. "-" when the request is not authenticated.',
  },
  {
    token: '[10/Oct/2024:13:55:36 +0000]',
    variable: '$time_local',
    description:
      'Local time the request finished, in Common Log Format (day/month/year:hour:minute:second zone).',
  },
  {
    token: '"GET /api/orders?id=42 HTTP/1.1"',
    variable: '$request',
    description:
      'The full request line: HTTP method, request URI (path + query string), and protocol version, exactly as sent by the client.',
  },
  {
    token: '200',
    variable: '$status',
    description:
      'HTTP response status code nginx sent back to the client (e.g. 200, 301, 404, 502).',
  },
  {
    token: '2326',
    variable: '$body_bytes_sent',
    description:
      'Number of bytes sent to the client in the response body, not counting the response headers.',
  },
  {
    token: '"https://example.com/cart"',
    variable: '$http_referer',
    description:
      'Value of the Referer request header — the page the client came from. "-" when absent.',
  },
  {
    token: '"Mozilla/5.0 (X11; Linux x86_64) ..."',
    variable: '$http_user_agent',
    description:
      'Value of the User-Agent request header — identifies the browser, bot, or client library.',
  },
];

// Error log line: <date time> [<level>] <pid>#<tid>: *<conn> <message>
export const ERROR_EXAMPLE =
  '2024/10/10 13:55:36 [error] 1234#1234: *567 connect() failed (111: Connection refused) while connecting to upstream, client: 192.0.2.10, server: example.com, request: "GET /api HTTP/1.1", upstream: "http://127.0.0.1:9000/api", host: "example.com"';

export const ERROR_FIELDS: LogField[] = [
  {
    token: '2024/10/10 13:55:36',
    variable: 'date time',
    description: 'Timestamp of the event (server local time), in YYYY/MM/DD HH:MM:SS format.',
  },
  {
    token: '[error]',
    variable: 'severity level',
    description:
      'Severity of the message. The threshold is controlled by the second argument to the error_log directive.',
  },
  {
    token: '1234#1234',
    variable: 'pid#tid',
    description: 'The worker process ID and thread ID that emitted the message.',
  },
  {
    token: '*567',
    variable: 'connection',
    description: 'Connection serial number — lets you correlate every log line for a single connection.',
  },
  {
    token: 'connect() failed (111: Connection refused) ...',
    variable: 'message',
    description:
      'Free-form message plus context key/value pairs nginx attaches (client, server, request, upstream, host).',
  },
];

// nginx error_log severity levels, least to most severe (lower includes higher).
export const ERROR_LEVELS: SeverityLevel[] = [
  { level: 'debug', meaning: 'Extremely verbose debugging detail (requires nginx built with --with-debug).' },
  { level: 'info', meaning: 'Informational messages about normal operation.' },
  { level: 'notice', meaning: 'Normal but notable events.' },
  { level: 'warn', meaning: 'Warnings — something unexpected, but the request was still served.' },
  { level: 'error', meaning: 'A request failed: an error occurred while processing it (the default error_log level).' },
  { level: 'crit', meaning: 'Critical conditions, such as failing to bind a socket.' },
  { level: 'alert', meaning: 'Action must be taken immediately; the server is in a bad state.' },
  { level: 'emerg', meaning: 'Emergency — nginx is unusable (e.g. configuration prevents startup).' },
];

export interface FileLocation {
  path: string;
  description: string;
}

export const DEFAULT_LOCATIONS: FileLocation[] = [
  { path: '/var/log/nginx/access.log', description: 'Default access log (Debian/Ubuntu and RHEL/CentOS packages).' },
  { path: '/var/log/nginx/error.log', description: 'Default error log.' },
  { path: '/usr/local/nginx/logs/access.log', description: 'Default when nginx is compiled from source with default --prefix.' },
  { path: '/usr/local/nginx/logs/error.log', description: 'Source-build error log.' },
  { path: '/etc/nginx/nginx.conf', description: 'Main config — look here for log_format and access_log / error_log directives.' },
];

export const CUSTOM_LOG_FORMAT_EXAMPLE = `# Define a custom format (e.g. add upstream timing) in the http {} block:
log_format timed_combined
    '$remote_addr - $remote_user [$time_local] '
    '"$request" $status $body_bytes_sent '
    '"$http_referer" "$http_user_agent" '
    'rt=$request_time uct="$upstream_connect_time" '
    'uht="$upstream_header_time" urt="$upstream_response_time"';

# Then reference it on an access_log directive (http, server, or location):
access_log /var/log/nginx/access.log timed_combined;`;

// ---------------------------------------------------------------------------
// SECTION 2 — Pattern library
// ---------------------------------------------------------------------------
// Helper context for the Alert24 config blocks below.
const ACCESS = '/var/log/nginx/access.log';
const ERROR = '/var/log/nginx/error.log';

export const PATTERNS: NginxPattern[] = [
  {
    id: 'nginx_5xx',
    name: '5xx server errors',
    log: 'access',
    regex: '" 5\\d\\d ',
    flags: 'g',
    catches:
      'Any 5xx response in the combined access log (500, 502, 503, 504…). Matches the status field that follows the quoted request line.',
    example:
      '192.0.2.10 - - [10/Oct/2024:13:55:36 +0000] "GET /api HTTP/1.1" 500 120 "-" "curl/8.0"',
    severity: 'critical',
    agentConfig: `{ "name": "nginx_5xx", "source": "file", "path": "${ACCESS}", "pattern": "\\" 5\\\\d\\\\d ", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_error_rate", "log_search": "nginx_5xx", "operator": "gt", "threshold": 5, "duration_seconds": 120, "severity": "high" }',
  },
  {
    id: 'nginx_4xx',
    name: '4xx client errors',
    log: 'access',
    regex: '" 4\\d\\d ',
    flags: 'g',
    catches:
      'Any 4xx client error (400, 401, 403, 404, 429…). Useful as a volume metric — a sudden 4xx spike often signals scanning or a broken deploy.',
    example:
      '198.51.100.7 - - [10/Oct/2024:13:55:37 +0000] "GET /wp-admin HTTP/1.1" 404 153 "-" "Mozilla/5.0"',
    severity: 'medium',
    agentConfig: `{ "name": "nginx_4xx", "source": "file", "path": "${ACCESS}", "pattern": "\\" 4\\\\d\\\\d ", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_rate", "log_search": "nginx_4xx", "operator": "gt", "threshold": 100, "duration_seconds": 300, "severity": "medium" }',
  },
  {
    id: 'nginx_502_504',
    name: 'Specific status codes (502 / 504)',
    log: 'access',
    regex: '" 50[24] ',
    flags: 'g',
    catches:
      'Bad-gateway (502) and gateway-timeout (504) responses specifically — the two codes that almost always mean the upstream/app server is down or too slow.',
    example:
      '192.0.2.10 - - [10/Oct/2024:13:55:38 +0000] "POST /checkout HTTP/1.1" 504 0 "-" "Mozilla/5.0"',
    severity: 'critical',
    agentConfig: `{ "name": "nginx_502_504", "source": "file", "path": "${ACCESS}", "pattern": "\\" 50[24] ", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_count", "log_search": "nginx_502_504", "operator": "gt", "threshold": 0, "duration_seconds": 0, "severity": "critical" }',
  },
  {
    id: 'nginx_slow_upstream',
    name: 'Slow upstream responses',
    log: 'access',
    regex: 'urt="(?:[1-9]\\d*\\.\\d+|\\d+\\.[5-9]\\d*)"',
    flags: 'g',
    catches:
      'Requests where $upstream_response_time (logged as urt="…") is roughly 0.5s or longer. Requires a custom log_format that includes urt="$upstream_response_time".',
    example:
      '192.0.2.10 - - [10/Oct/2024:13:55:39 +0000] "GET /report HTTP/1.1" 200 8123 "-" "Mozilla/5.0" rt=2.512 uct="0.001" uht="2.500" urt="2.500"',
    severity: 'high',
    agentConfig: `{ "name": "nginx_slow_upstream", "source": "file", "path": "${ACCESS}", "pattern": "urt=\\"(?:[1-9]\\\\d*\\\\.\\\\d+|\\\\d+\\\\.[5-9]\\\\d*)\\"", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_rate", "log_search": "nginx_slow_upstream", "operator": "gt", "threshold": 10, "duration_seconds": 120, "severity": "high" }',
  },
  {
    id: 'nginx_path_login',
    name: 'Requests to a specific path (/login)',
    log: 'access',
    regex: '"(?:GET|POST) /login(?:[ ?])',
    flags: 'g',
    catches:
      'Hits on a sensitive path such as /login. Swap the path to monitor /admin, /api/keys, etc. Pair with a status filter to count failed logins.',
    example:
      '203.0.113.5 - - [10/Oct/2024:13:55:40 +0000] "POST /login HTTP/1.1" 401 12 "-" "python-requests/2.31"',
    severity: 'medium',
    agentConfig: `{ "name": "nginx_path_login", "source": "file", "path": "${ACCESS}", "pattern": "\\"(?:GET|POST) /login(?:[ ?])", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_count", "log_search": "nginx_path_login", "operator": "gt", "threshold": 50, "duration_seconds": 60, "severity": "medium" }',
  },
  {
    id: 'nginx_large_body',
    name: 'Large response bodies (>1 MB)',
    log: 'access',
    regex: '" [1-5]\\d\\d \\d{7,} ',
    flags: 'g',
    catches:
      'Responses whose $body_bytes_sent is 7+ digits (≥1,000,000 bytes ≈ 1 MB). Helps spot accidental full-table exports or unthrottled downloads.',
    example:
      '192.0.2.10 - - [10/Oct/2024:13:55:41 +0000] "GET /export.csv HTTP/1.1" 200 5242880 "-" "Mozilla/5.0"',
    severity: 'low',
    agentConfig: `{ "name": "nginx_large_body", "source": "file", "path": "${ACCESS}", "pattern": "\\" [1-5]\\\\d\\\\d \\\\d{7,} ", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_rate", "log_search": "nginx_large_body", "operator": "gt", "threshold": 20, "duration_seconds": 300, "severity": "low" }',
  },
  {
    id: 'nginx_bots_scanners',
    name: 'Bot / scanner user-agents',
    log: 'access',
    regex: '(?:sqlmap|nikto|nmap|masscan|nuclei|acunetix|wpscan|dirbuster|gobuster|zgrab|censys|curl|python-requests|Go-http-client)',
    flags: 'gi',
    catches:
      'Common scanner and automated-client user-agent strings. Tune the list to your environment — legitimate tooling (curl) may appear too.',
    example:
      '198.51.100.7 - - [10/Oct/2024:13:55:42 +0000] "GET /?id=1%27 HTTP/1.1" 403 0 "-" "sqlmap/1.8#stable (https://sqlmap.org)"',
    severity: 'high',
    agentConfig: `{ "name": "nginx_bots_scanners", "source": "file", "path": "${ACCESS}", "pattern": "(?:sqlmap|nikto|nmap|masscan|nuclei|acunetix|wpscan|dirbuster|gobuster|zgrab|censys|python-requests|Go-http-client)", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_count", "log_search": "nginx_bots_scanners", "operator": "gt", "threshold": 5, "duration_seconds": 60, "severity": "high" }',
  },
  {
    id: 'nginx_upstream_timeout',
    name: 'Upstream timed out',
    log: 'error',
    regex: 'upstream timed out \\(110: (?:Connection timed out|Unknown error)\\)',
    flags: 'g',
    catches:
      'The classic "upstream timed out" error — nginx gave up waiting for the backend (proxy_read_timeout / proxy_connect_timeout exceeded). Frequently precedes 504s.',
    example:
      '2024/10/10 13:55:43 [error] 1234#1234: *890 upstream timed out (110: Connection timed out) while reading response header from upstream, client: 192.0.2.10, server: example.com, request: "GET /slow HTTP/1.1", upstream: "http://127.0.0.1:9000/slow"',
    severity: 'high',
    agentConfig: `{ "name": "nginx_upstream_timeout", "source": "file", "path": "${ERROR}", "pattern": "upstream timed out \\\\(110: ", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_count", "log_search": "nginx_upstream_timeout", "operator": "gt", "threshold": 0, "duration_seconds": 60, "severity": "high" }',
  },
  {
    id: 'nginx_conn_refused',
    name: 'Connection refused (upstream down)',
    log: 'error',
    regex: 'connect\\(\\) failed \\(111: Connection refused\\)',
    flags: 'g',
    catches:
      'nginx could not open a connection to the upstream — the backend process is not listening on the configured address/port. Usually means the app crashed or was never started.',
    example:
      '2024/10/10 13:55:44 [error] 1234#1234: *891 connect() failed (111: Connection refused) while connecting to upstream, client: 192.0.2.10, server: example.com, request: "GET /api HTTP/1.1", upstream: "http://127.0.0.1:9000/api"',
    severity: 'critical',
    agentConfig: `{ "name": "nginx_conn_refused", "source": "file", "path": "${ERROR}", "pattern": "connect\\\\(\\\\) failed \\\\(111: Connection refused\\\\)", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_count", "log_search": "nginx_conn_refused", "operator": "gt", "threshold": 0, "duration_seconds": 0, "severity": "critical" }',
  },
  {
    id: 'nginx_worker_connections',
    name: 'worker_connections are not enough',
    log: 'error',
    regex: 'worker_connections are not enough',
    flags: 'g',
    catches:
      'nginx ran out of worker connections — raise the worker_connections directive (and the OS file-descriptor limit). New clients are being dropped while this fires.',
    example:
      '2024/10/10 13:55:45 [alert] 1234#1234: *892 worker_connections are not enough while connecting to upstream, client: 192.0.2.10, server: example.com, request: "GET / HTTP/1.1"',
    severity: 'critical',
    agentConfig: `{ "name": "nginx_worker_connections", "source": "file", "path": "${ERROR}", "pattern": "worker_connections are not enough", "pattern_type": "contains" }`,
    alertRule:
      '{ "metric": "log_match_count", "log_search": "nginx_worker_connections", "operator": "gt", "threshold": 0, "duration_seconds": 0, "severity": "critical" }',
  },
  {
    id: 'nginx_ssl_handshake',
    name: 'SSL handshake errors',
    log: 'error',
    regex: 'SSL_do_handshake\\(\\) failed',
    flags: 'g',
    catches:
      'A TLS handshake failed (bad cipher, unsupported protocol version, client abort, or a non-HTTPS request hitting an HTTPS port). A trickle is normal; a spike signals a scanner or misconfigured client.',
    example:
      '2024/10/10 13:55:46 [info] 1234#1234: *893 SSL_do_handshake() failed (SSL: error:0A00006C:SSL routines::bad key share) while SSL handshaking, client: 198.51.100.7, server: 0.0.0.0:443',
    severity: 'medium',
    agentConfig: `{ "name": "nginx_ssl_handshake", "source": "file", "path": "${ERROR}", "pattern": "SSL_do_handshake\\\\(\\\\) failed", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_rate", "log_search": "nginx_ssl_handshake", "operator": "gt", "threshold": 30, "duration_seconds": 120, "severity": "medium" }',
  },
  {
    id: 'nginx_rate_limit',
    name: 'Rate limiting (limiting requests)',
    log: 'error',
    regex: 'limiting requests, excess: [\\d.]+ by zone',
    flags: 'g',
    catches:
      'The limit_req module is throttling clients. Each line names the zone and the excess rate. A high volume means your rate limit is biting (DoS, scraping, or a too-tight limit).',
    example:
      '2024/10/10 13:55:47 [error] 1234#1234: *894 limiting requests, excess: 5.300 by zone "one", client: 203.0.113.5, server: example.com, request: "GET /api HTTP/1.1", host: "example.com"',
    severity: 'medium',
    agentConfig: `{ "name": "nginx_rate_limit", "source": "file", "path": "${ERROR}", "pattern": "limiting requests, excess:", "pattern_type": "contains" }`,
    alertRule:
      '{ "metric": "log_match_rate", "log_search": "nginx_rate_limit", "operator": "gt", "threshold": 50, "duration_seconds": 120, "severity": "medium" }',
  },
  {
    id: 'nginx_all_errors',
    name: 'All error-level and worse',
    log: 'error',
    regex: '\\[(?:error|crit|alert|emerg)\\]',
    flags: 'g',
    catches:
      'Every error log line at severity error or higher. A broad catch-all for an "is anything broken?" volume metric across the whole error log.',
    example:
      '2024/10/10 13:55:48 [crit] 1234#1234: *895 SSL_write() failed while sending response to client, client: 192.0.2.10, server: example.com',
    severity: 'high',
    agentConfig: `{ "name": "nginx_all_errors", "source": "file", "path": "${ERROR}", "pattern": "\\\\[(?:error|crit|alert|emerg)\\\\]", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_rate", "log_search": "nginx_all_errors", "operator": "gt", "threshold": 20, "duration_seconds": 120, "severity": "high" }',
  },
  {
    id: 'nginx_no_live_upstreams',
    name: 'No live upstreams',
    log: 'error',
    regex: 'no live upstreams while connecting to upstream',
    flags: 'g',
    catches:
      'Every server in an upstream {} pool is marked down — nginx has no healthy backend to forward to. Almost always a full backend outage; emits 502s to clients.',
    example:
      '2024/10/10 13:55:49 [error] 1234#1234: *896 no live upstreams while connecting to upstream, client: 192.0.2.10, server: example.com, request: "GET /api HTTP/1.1", upstream: "http://backend/api", host: "example.com"',
    severity: 'critical',
    agentConfig: `{ "name": "nginx_no_live_upstreams", "source": "file", "path": "${ERROR}", "pattern": "no live upstreams", "pattern_type": "contains" }`,
    alertRule:
      '{ "metric": "log_match_count", "log_search": "nginx_no_live_upstreams", "operator": "gt", "threshold": 0, "duration_seconds": 0, "severity": "critical" }',
  },
  {
    id: 'nginx_too_many_files',
    name: 'Too many open files',
    log: 'error',
    regex: '\\(24: Too many open files\\)',
    flags: 'g',
    catches:
      'The worker hit its file-descriptor limit (errno 24). Raise worker_rlimit_nofile and the systemd/ulimit LimitNOFILE. Connections and upstreams fail while this fires.',
    example:
      '2024/10/10 13:55:50 [crit] 1234#1234: *897 accept4() failed (24: Too many open files)',
    severity: 'critical',
    agentConfig: `{ "name": "nginx_too_many_files", "source": "file", "path": "${ERROR}", "pattern": "\\\\(24: Too many open files\\\\)", "pattern_type": "regex" }`,
    alertRule:
      '{ "metric": "log_match_count", "log_search": "nginx_too_many_files", "operator": "gt", "threshold": 0, "duration_seconds": 0, "severity": "critical" }',
  },
];

export const ALERT_RULES_DOC_NOTE =
  'Add the alert_rules entry to the existing "alert_rules" array in the same agent config. log_match_count = matches in the interval; log_match_rate = matches/min; log_error_rate = % of lines matching (great for the 5xx pattern). Rate metrics need a non-zero interval_seconds on the heartbeat.';
